import { motion } from 'framer-motion';
import aliensImg from '../../assests/all-alien.png';
import aboutIcon from '../../assests/about.png';

const bioText = [
  `I build and ship AI products that combine large language models, autonomous agents, retrieval systems, and modern web technologies into practical applications. From document-grounded AI assistants and voice-enabled task agents to automation workflows and full-stack platforms, I'm interested in taking ideas from prototype to production with a strong focus on usability and reliability.`,
  `My work revolves around LLM engineering, RAG pipelines, LangChain, OpenAI and Gemini APIs, and AI automation using tools like n8n and Make. I've built systems that allow users to chat with private documents, autonomous voice assistants that manage tasks through phone calls, self-hosted AI knowledge platforms, and content generation pipelines that operate without manual intervention.`,
  `Alongside AI development, I enjoy building scalable backend services and intuitive frontend experiences using React, Node.js, and Python. I'm driven by building products rather than experiments. I enjoy breaking down complex problems, designing robust architectures, and iterating quickly until an idea becomes something people can actually use.`,
  `Currently pursuing a B.Tech while actively seeking AI Engineering, Machine Learning, and Software Development opportunities where I can contribute, learn from experienced teams, and build impactful AI products at scale.`,
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.1 },
  }),
};

export default function About() {
  return (
    <section id="about" className="py-24 md:py-32 relative">
      {/* Section heading */}
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="flex items-center gap-3 mb-4"
        >
          <img src={aboutIcon} alt="About" className="w-7 h-7 object-contain" />
          <span
            className="text-white text-xs tracking-[0.3em] uppercase section-label"
          >
            About
          </span>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="text-white font-bold mb-16 max-w-2xl leading-tight"
          style={{ fontSize: 'clamp(0.85rem, 1.5vw, 1rem)', letterSpacing: '0.05em', textTransform: 'uppercase' }}
        >
          Product-focused AI Engineer building intelligent<br />
          systems and autonomous software
        </motion.p>

        {/* Two column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* Left — Aliens image */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="flex justify-center lg:justify-start"
          >
            <div className="relative">
              {/* Glow behind aliens */}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: 'radial-gradient(ellipse 70% 60% at 50% 60%, rgba(0,185,46,0.08) 0%, transparent 70%)',
                  filter: 'blur(20px)',
                  transform: 'scale(1.2)',
                }}
              />
              <img
                src={aliensImg}
                alt="Ben 10 Aliens"
                className="relative w-full max-w-[400px] lg:max-w-[480px] object-contain"
              />
            </div>
          </motion.div>

          {/* Right — Bio text */}
          <div className="space-y-5">
            <motion.h2
              custom={0}
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="text-white font-semibold text-lg mb-2"
            >
              MD Toufique
            </motion.h2>

            {bioText.map((para, i) => (
              <motion.p
                key={i}
                custom={i + 1}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="text-white/60 text-sm leading-[1.9] tracking-wide"
              >
                {para}
              </motion.p>
            ))}

            {/* Quick stats */}
            <motion.div
              custom={bioText.length + 2}
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid grid-cols-3 gap-4 pt-6 border-t border-white/10"
            >
              {[
                { label: 'AI Projects', value: '10+' },
                { label: 'Tech Stack', value: '10+' },
                { label: 'Building Experience', value: '6 months' },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <div
                    className="text-[#00B92E] font-bold text-2xl font-bebas"
                  >
                    {stat.value}
                  </div>
                  <div className="text-white/40 text-xs mt-1">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}